import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import PaymentMethodDialog from 'components/PaymentConsent/PaymentMethodDialog';
import { useTypedSelector, useAppDispatch } from 'combineReducers';
import { IPendingConnection, IConnectionBillingPreview, ICompany, ConnectionType } from 'interfaces/index';
import { ConnectionsBillingPreviewPayload, VerifySufficientFundsPayload } from 'interfaces/paymentMethods';
import { convertPending } from 'helpers/billingConverter';
import { verifySufficientFunds } from 'actions/paymentMethods';
import { applyIncomingConnections } from 'actions/connections';
import { cleanInsufficientFundsError } from 'actions/billingErrors';

interface Props {
  open: boolean;
  companyId: string;
  handleClose(): void;
  handleApply(): void;
  fetchPreview(
    selectedPendingConnections: IPendingConnection[],
    organizationDetails: ICompany
  ): Promise<ConnectionsBillingPreviewPayload | null>;
  refreshPendingConnections(): Promise<IPendingConnection[]>;
}

export const PaymentMethod: React.FC<Props> = ({
  open,
  companyId,
  refreshPendingConnections,
  fetchPreview,
  handleClose,
  handleApply
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [pendingLoading, setPendingLoading] = React.useState<boolean>(false);
  const [pendingConnect, setPendingConnect] = React.useState<boolean>(false);
  const [connectionsPreview, setConnectionsPreview] = React.useState<IConnectionBillingPreview[]>([]);

  const {
    isFetching: loadingPayment,
    hasSufficientFunds,
    pendingConnectionsBillingPreview,
    currentSelected
  } = useTypedSelector(state => state.paymentMethods);
  const organizationDetails = useTypedSelector(state => state.organizationDetails.organizationDetails);
  const { taxes, total, connections } = pendingConnectionsBillingPreview;
  const { isFetching: loadingContacts } = useTypedSelector(state => state.companyContact);
  const { allPendingConnections, activeConnectionsCount } = useTypedSelector(state => state.connections);
  const { insufficientFundsError } = useTypedSelector(state => state.billingErrors);

  const isAnyFetching = loadingContacts || loadingPayment || pendingLoading || pendingConnect;
  const incomingConnections = connectionsPreview.filter(connections => connections.type === ConnectionType.Incoming);

  const getConfirmMessage = () => {
    if (incomingConnections.length) {
      return incomingConnections.length === 1 ? (
        <Trans i18nKey="subscriber:incomingConnections.confirmOne">
          Upon clicking the button below, your selected payment method will be charged for the{' '}
          <strong>incoming connection only</strong>.
        </Trans>
      ) : (
        <Trans i18nKey="subscriber:incomingConnections.confirmMulti">
          Upon clicking the button below, your selected payment method will be charged for the{' '}
          <strong>incoming connections only</strong>.
        </Trans>
      );
    } else return null;
  };

  const renderConfirmButton = () => (
    <Button
      data-testid="Confirm"
      onClick={incomingConnections.length ? handleApplyIncomingConnections : handleApplyOutgoingConnections}
      color="primary"
      variant="contained"
      disabled={!hasSufficientFunds || isAnyFetching}
    >
      {incomingConnections.length
        ? t('subscriber:incomingConnections.processBtn', 'SAVE & PROCESS PAYMENT')
        : t('subscriber:outgoingConnections.confirmBtn', 'Confirm')}
    </Button>
  );

  const refreshPreview = async () => {
    if (!organizationDetails) {
      return;
    }
    setPendingLoading(true);
    const allPendingConnections = await refreshPendingConnections();

    const preview = await fetchPreview(allPendingConnections, organizationDetails);
    setPendingLoading(false);
    return preview as ConnectionsBillingPreviewPayload;
  };

  const getSufficientFundsPayload = React.useCallback(
    async (total: number) => {
      const payload = await dispatch(
        verifySufficientFunds({
          companyId,
          amount: total,
          paymentMethodId: currentSelected.id
        })
      );
      setPendingLoading(false);

      return (payload as unknown) as VerifySufficientFundsPayload;
    },
    [currentSelected, companyId, dispatch]
  );

  const handleApplyOutgoingConnections = async () => {
    const preview = await refreshPreview();
    if (preview) {
      const payload = await getSufficientFundsPayload(preview?.total ?? total);

      if (payload && payload.hasSufficientFunds) {
        handleClose();
        handleApply();
      }
    }
  };

  const handleApplyIncomingConnections = async () => {
    setPendingConnect(true);
    const preview = await refreshPreview();
    if (preview) {
      const payload = await getSufficientFundsPayload(preview?.total ?? total);

      if (payload && payload.hasSufficientFunds) {
        const incomingConnectionsIds = incomingConnections.map(connection => connection.organizationId);

        organizationDetails &&
          dispatch(
            applyIncomingConnections({
              incomingConnectionsIds,
              organizationId: organizationDetails.aionOrganizationId,
              afterSuccess: () => {
                setPendingConnect(false);
                dispatch(cleanInsufficientFundsError());
                handleClose();
                handleApply();
              },
              afterError: () => {
                setPendingConnect(false);
                if (insufficientFundsError.data) {
                  refreshPreview();
                }
              }
            })
          );
      }
    }
  };

  const setPreview = React.useCallback(() => {
    if (!connections.length) {
      return;
    }
    setConnectionsPreview(convertPending(allPendingConnections, connections, activeConnectionsCount));
  }, [allPendingConnections, activeConnectionsCount, connections]);

  React.useEffect(() => setPreview(), [setPreview]);

  return (
    <PaymentMethodDialog
      open={open}
      companyId={companyId}
      connections={connectionsPreview}
      total={total}
      taxes={taxes}
      loading={pendingLoading || pendingConnect}
      handleClose={handleClose}
      renderConfirmButton={renderConfirmButton}
      refreshPreview={refreshPreview}
      confirmMessage={getConfirmMessage()}
    />
  );
};
